import type { QueryBuilder } from "@/lib/backend";

/**
 * Ordering for the server-paginated lists.
 *
 * Every list here is read one `.range()` window at a time, and a window is only
 * meaningful if the full result has ONE order. `ORDER BY date DESC` does not:
 * rows sharing a date may come back in any order, and Postgres is free to pick
 * a different one per query, so a row inside a tie group that straddles a page
 * boundary can appear on both sides of it — or on neither.
 *
 * That is not theoretical here. `gl_products` holds 1,343 live rows across 3
 * distinct `created_at` values, so the CSV export's 500-row pages both split a
 * tie group: walking it returned 1,343 rows of which 1,198 were distinct, with
 * 145 products duplicated and 145 missing from the file entirely, under a
 * footer confidently reading "1,343 Products". `gl_shipping_records` is worse
 * still — every `date` is NULL, so the default sort is a single 1,741-row tie.
 *
 * The fix is a unique final sort key. `id` is the primary key on every one of
 * these relations, so appending it makes the order total and every window a
 * genuine slice of one sequence.
 */

/** The unique column every paged list order ends with. */
const LIST_ORDER_TIEBREAK_COLUMN = "id";

/**
 * Appends the unique tiebreaker to a query that already carries its own
 * ordering — for a list whose leading sort keys are decided elsewhere, such as
 * the purchase-order merge-target lookup.
 */
export function tiebreakListOrder<T>(query: QueryBuilder<T>): QueryBuilder<T> {
  return query.order(LIST_ORDER_TIEBREAK_COLUMN, { ascending: false });
}

/**
 * Orders a paged list by the caller's column, then by the tiebreaker.
 *
 * This is the ordering every paged list query applies, including the ones the
 * CSV export and "select all matching" walk: `fetchAllFilteredRows` runs the
 * same query with successive offsets, so a partial order there loses and
 * duplicates rows silently.
 */
export function orderListPage<T>(
  query: QueryBuilder<T>,
  sort: { ascending: boolean; column: string },
): QueryBuilder<T> {
  return tiebreakListOrder(query.order(sort.column, { ascending: sort.ascending }));
}
